import React from 'react';
import styled from 'styled-components';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationContainer = styled.nav.attrs({ 'aria-label': '페이지 이동' })`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 0;
  padding: 12px 0;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.color.point_y_100 : theme.color.primary_300};
  background-color: ${({ theme, $active }) =>
    $active ? theme.color.point_y_100 : theme.color.primary_100};
  color: ${({ theme, $active }) =>
    $active ? (theme.name === 'dark' ? '#101828' : '#FFFFFF') : theme.color.text_color_200};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.color.point_y_100};
    color: ${({ theme, $active }) => $active ? (theme.name === 'dark' ? '#101828' : '#FFFFFF') : theme.color.point_y_100};
    background-color: ${({ theme, $active }) =>
      $active ? theme.color.point_y_100 : theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NavButton = styled(PageButton)`
  width: auto;
  padding: 0 12px;
`;

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const PAGE_LIMIT = 10;
  const startPage = Math.floor((currentPage - 1) / PAGE_LIMIT) * PAGE_LIMIT + 1;
  const endPage = Math.min(startPage + PAGE_LIMIT - 1, totalPages);
  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <PaginationContainer>
      <NavButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || totalPages === 0}
      >
        이전
      </NavButton>
      {pages.map(page => (
        <PageButton
          key={page}
          $active={page === currentPage}
          aria-current={page === currentPage ? 'page' : undefined}
          onClick={() => onPageChange(page)}
        >
          {page}
        </PageButton>
      ))}
      <NavButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || totalPages === 0}
      >
        다음
      </NavButton>
    </PaginationContainer>
  );
};

export default Pagination;
